'use client';

import { useState, useRef } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import Script from 'next/script';

const FeedbackWidget = () => {
    const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    const [isOpen, setIsOpen] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const recaptchaRef = useRef<ReCAPTCHA>(null);

    const handleRecaptchaChange = (token: string | null) => {
        setRecaptchaToken(token);
        setError(''); // Limpia error si se resuelve
    };

    const handleSubmit = async () => {
        if (!feedback.trim()) return; if (!feedback.trim()) return;
        if (!recaptchaToken) {
            setError('Please complete the reCAPTCHA.');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            await axios.post('/api/feedback', {
                feedback,
                email,
                recaptchaToken
            });

            setSuccess(true);
            setFeedback('');
            setEmail('');
            setRecaptchaToken(null);
            recaptchaRef.current?.reset();

            setTimeout(() => {
                setSuccess(false);
                setIsOpen(false);
            }, 2000);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to send feedback. Please try again.');
            recaptchaRef.current?.reset();
            setRecaptchaToken(null);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Script
                src="https://www.google.com/recaptcha/api.js"
                strategy="afterInteractive"
            />
            <div className="fixed bottom-6 right-6 z-50">
                {isOpen ? (
                    <div className="bg-white rounded-lg shadow-xl border border-slate-200 w-80 overflow-hidden">
                        <div className="bg-emerald-600 px-4 py-2 flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <MessageCircle className="w-5 h-5 text-white" />
                                <h3 className="text-white font-medium">Feedback</h3>
                            </div>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="text-white hover:text-slate-100 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-4">
                            {success ? (
                                <div className="text-center py-6">
                                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <Send className="w-6 h-6 text-emerald-600" />
                                    </div>
                                    <p className="text-slate-700 font-medium">Thanks for your feedback!</p>
                                </div>
                            ) : (
                                <>
                                    <p className="text-sm text-slate-600 mb-3">Hi 👋</p>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Your email (optional)"
                                        className="w-full border border-slate-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-emerald-500/25 focus:border-emerald-500 mb-3"
                                        disabled={isLoading}
                                    />
                                    <textarea
                                        value={feedback}
                                        onChange={(e) => setFeedback(e.target.value)}
                                        placeholder="We would like to hear your feedback, improvements, bugs etc."
                                        className="w-full border border-slate-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-emerald-500/25 focus:border-emerald-500 resize-none"
                                        rows={4}
                                        disabled={isLoading}
                                    />
                                    {error && (
                                        <p className="text-xs text-red-600 mt-2">{error}</p>
                                    )}

                                    <div className="mb-3">
                                        {recaptchaSiteKey ? (
                                            <ReCAPTCHA
                                                sitekey={recaptchaSiteKey}
                                                onChange={handleRecaptchaChange}
                                                ref={recaptchaRef}
                                            />
                                        ) : (
                                            <p className="text-xs text-red-600">
                                                ReCAPTCHA is not configured. Missing `NEXT_PUBLIC_RECAPTCHA_SITE_KEY`.
                                            </p>
                                        )}
                                    </div>

                                    <button
                                        onClick={handleSubmit}
                                        disabled={isLoading || !feedback.trim()}
                                        className="w-full mt-3 bg-emerald-600 text-white py-2 rounded-md font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? 'Sending...' : 'Send'}
                                        <Send className="w-4 h-4" />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                    <button
                        onClick={() => setIsOpen(true)}
                        className="bg-emerald-600 text-white rounded-full p-4 shadow-lg hover:bg-emerald-700 transition-all hover:scale-110"
                    >
                        <MessageCircle className="w-6 h-6" />
                    </button>
                )}
            </div>
        </>
    );
};

export default FeedbackWidget;